import { useMemo, useState } from "react";
import { InlineSpinner } from "../components/InlineSpinner";
import { ErrorPage, InlineError, LoadingPage, StudioShell } from "../components/StudioShell";
import { useWork } from "../lib/api";
import { useAutosave } from "../lib/autosave";
import { navigate } from "../lib/navigation";
import { loadSession, pathForStep } from "../lib/session";

export function ModePage({ workId }: { workId: string }) {
  const { data, loading, error } = useWork(workId);
  const [moving, setMoving] = useState(false);
  const payload = useMemo(() => ({ step: "mode" as const }), []);
  const autosave = useAutosave(workId, payload);
  const hummingEnabled = loadSession()?.hummingEnabled ?? false;

  if (loading) return <LoadingPage />;
  if (!data || error) {
    return <ErrorPage message={error?.message ?? "작품을 불러오지 못했습니다."} />;
  }

  const chooseLyrics = async (): Promise<void> => {
    setMoving(true);
    try {
      await autosave.flush({ step: "lyrics", mode: "lyrics" });
      navigate(pathForStep(workId, "lyrics"));
    } finally {
      setMoving(false);
    }
  };

  const chooseHumming = async (): Promise<void> => {
    setMoving(true);
    try {
      await autosave.flush({ step: "humming", mode: "humming" });
      navigate(pathForStep(workId, "humming"));
    } finally {
      setMoving(false);
    }
  };

  return (
    <StudioShell currentStep={1} remainingSlots={data.remaining_slots} autosaveStatus={autosave.status}>
      <div className="mode-page-content">
        <h1 tabIndex={-1}>어떻게 노래를 만들까요?</h1>
        <p className="page-lead">내 이야기를 가사로 쓰면 진짜 노래 한 곡이 완성돼요</p>
        <div className={`mode-grid${hummingEnabled ? "" : " mode-grid-single"}`}>
          <button className="mode-card" type="button" onClick={() => void chooseLyrics()} disabled={moving}>
            <span className="mode-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
            </span>
            <strong>가사로 만들기</strong>
            <span>이야기를 가사로 쓰면 AI가 곡을 붙여요. 처음이라면 이 방법을 추천해요.</span>
          </button>
          {hummingEnabled ? (
            <button className="mode-card" type="button" onClick={() => void chooseHumming()} disabled={moving}>
              <span className="mode-icon" aria-hidden="true">🎙</span>
              <strong>허밍으로 만들기</strong>
              <span>내가 부른 멜로디를 바탕으로 노래를 만들어요. 가사는 선택이에요.</span>
            </button>
          ) : null}
        </div>
        {/* 카드형 버튼이라 글자를 갈아 끼울 자리가 없다 — 격자 아래에 한 줄로 알린다.
            종전에는 카드가 회색이 될 뿐이라 눌렸는지조차 알기 어려웠다(2026-08-13 jery 4번). */}
        {moving ? <p className="mode-moving" role="status"><InlineSpinner label="다음 화면을 여는 중..." /></p> : null}
        <p className="mode-caption">마이크가 없으면 가사로 만들기를 골라요</p>
        {autosave.status === "error" ? <InlineError>연결을 확인한 뒤 다시 눌러 주세요.</InlineError> : null}
      </div>
    </StudioShell>
  );
}
